import { useState } from "react";
import { Box, IconButton, Typography } from "@mui/material";
import ReplayCircleFilledIcon from "@mui/icons-material/ReplayCircleFilled";

import theme from "themeProvider";
import { TooltipLabel } from "components";
import { GridSkeleton } from "CustomStyledComponents";

import HoverBlock from "./HoverBlock";
import FileUpload from "./FileUpload";
import { ColumnView, ViewableItem } from "./CustomDetailComponents";

import { pascalCase, validateNull, validateErrorMsg } from "utils";
import { postCall_v2 } from "services";
import { useToaster } from "hooks";
import { success, error } from "constants";

const SellerTrack = ({ orderInfo }) => {
  const triggerToaster = useToaster();
  const [isPoRetryLocked, setIsPoRetryLocked] = useState(false);

  const getSellerInfo = (sellerDetails) => {
    if (sellerDetails) {
      const { sellerKey } = sellerDetails;
      let sellerInfo = orderInfo?.sellerDetails[sellerKey];
      let address;
      if (sellerInfo?.address) {
        const { streetLine, city, state, postalCode } = sellerInfo?.address;
        address = `${streetLine ? streetLine + ",\n" : ""} ${
          city ? city + ", " : ""
        } ${state ? state + ", " : ""} ${postalCode ?? ""}`;
      }

      let formattedSellerInfo = {
        Name: orderInfo?.sellerDetails[sellerKey]?.name,
        ...(orderInfo?.sellerDetails[sellerKey]?.gstin && {
          GSTIN: orderInfo?.sellerDetails[sellerKey]?.gstin,
        }),
      };
      if (address) return { ...formattedSellerInfo, Address: address };
      return formattedSellerInfo;
    }
  };

  const retryJodlOrderCreation = async () => {
    try {
      const listOfSellerOrder = orderInfo?.childOrders?.map((ele) => {
        return ele?.sellerOrderNumber;
      });

      const payload = {
        listOfSellerOrder,
      };

      const res = await postCall_v2(
        `joms/api/external/v1/sendSellerOrderToConnector`,
        payload,
      );
      if (res) {
        triggerToaster("JODL order creation reinitiated successfully", success);
      }
    } catch (err) {
      triggerToaster(
        err?.message ?? "JODL order creation reinitiated failed",
        error,
      );
    }
  };

  const retryJodlPoCreation = async () => {
    if (isPoRetryLocked) return;

    const sellerOrderNumber =
      orderInfo?.autoPODetails?.[0]?.jodlSellerOrderNumber;

    if (!sellerOrderNumber) {
      triggerToaster("JODL Seller Order Number not found", error);
      return;
    }

    try {
      setIsPoRetryLocked(true);

      const payload = {
        request: {
          sellerOrderNumber,
        },
        autoPOActionType: "PO_FAILED",
      };

      const res = await postCall_v2(
        "joms/api/external/auto-po-creation",
        payload,
      );

      if (res?.data?.isSuccess) {
        triggerToaster(
          res?.data?.message ?? "JODL PO creation reinitiated successfully",
          success,
        );
      } else {
        triggerToaster(
          res?.data?.message ?? "JODL PO reinitiated failed",
          error,
        );
      }
    } catch (err) {
      triggerToaster(
        validateErrorMsg(err?.data?.message) ??
          "Unexpected error came to retry",
        error,
      );
    } finally {
      setIsPoRetryLocked(false);
    }
  };

  return (
    <>
      <Box mb={8}>
        {orderInfo?.childOrders?.map((ele, ind) => {
          return (
            <Box
              mb={orderInfo?.childOrders.length - 1 !== ind ? 4 : 0}
              key={ele?.sellerOrderNumber}
              display={"flex"}
              flexDirection={"column"}
            >
              <GridSkeleton
                firstChild={
                  <ViewableItem
                    label={"Seller order no."}
                    value={ele?.sellerOrderNumber}
                  />
                }
                secondChild={
                  <ColumnView label={"Seller"}>
                    <TooltipLabel
                      placement="right"
                      title={<HoverBlock data={getSellerInfo(ele)} />}
                      labelChildren={
                        <Typography
                          fontSize={16}
                          fontWeight={"bold"}
                          color={theme.palette.secondary.main}
                          style={{ wordBreak: "break-word" }}
                        >
                          {pascalCase(ele?.sellerName)}
                        </Typography>
                      }
                    />
                  </ColumnView>
                }
                thirdChild={
                  <ViewableItem
                    label={"Seller order status"}
                    value={ele?.currentState}
                  />
                }
              />
              {!!ele?.files &&
                !!Object.keys(ele.files).length &&
                Object.keys(ele.files).map((type, index) => (
                  <Box width={"80%"} mt={2} key={index + type}>
                    <FileUpload
                      fileDetails={ele?.files?.[type]}
                      type={type}
                      orderNumber={ele?.sellerOrderNumber}
                    />
                  </Box>
                ))}
            </Box>
          );
        })}
      </Box>
      <GridSkeleton
        firstChild={
          <ViewableItem
            label={"JODL order no."}
            value={validateNull(orderInfo?.jodlOrderNumber)}
          />
        }
        secondChild={
          <Box
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "baseline",
            }}
          >
            <Typography>{"JODL order status"}</Typography>
            <Typography style={{ fontWeight: "bold", marginTop: 6 }}>
              {validateNull(orderInfo?.jodlOrderStatus)}
            </Typography>
            <Box display={"flex"} alignItems={"center"}>
              <Typography variant="body2">
                {orderInfo?.jodlFailedReason}
              </Typography>
              {orderInfo?.jodlOrderRetry && (
                <IconButton onClick={retryJodlOrderCreation}>
                  <ReplayCircleFilledIcon
                    sx={{
                      color: theme.palette.secondary.main,
                      fontSize: 20,
                    }}
                  />
                </IconButton>
              )}
            </Box>
          </Box>
        }
        thirdChild={
          <Box
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "baseline",
            }}
          >
            <Box display="flex" alignItems="center">
              <Typography>{"JODL PO status"}</Typography>

              {orderInfo?.isJodlPoRetry && (
                <IconButton
                  onClick={retryJodlPoCreation}
                  disabled={isPoRetryLocked}
                >
                  <ReplayCircleFilledIcon
                    sx={{
                      color: isPoRetryLocked
                        ? theme.palette.action.disabled
                        : theme.palette.secondary.main,
                      fontSize: 20,
                      ml: 1,
                    }}
                  />
                </IconButton>
              )}
            </Box>

            {!orderInfo?.autoPODetails ||
            orderInfo.autoPODetails.length === 0 ? (
              <Typography mt={1}>-</Typography>
            ) : (
              orderInfo?.autoPODetails?.map((po, index) => (
                <Box
                  key={index}
                  mt={1}
                  display="flex"
                  alignItems="center"
                  gap={2}
                  width="100%"
                >
                  <Typography style={{ fontWeight: "bold" }}>
                    {validateNull(po?.jodlPoNumber || po?.erpPoNumber)}
                  </Typography>

                  <Typography variant="body2">
                    {validateNull(po?.actionType)}
                  </Typography>

                  <Typography variant="body2">
                    {validateNull(po?.message)}
                  </Typography>
                </Box>
              ))
            )}
          </Box>
        }
      />
    </>
  );
};

export default SellerTrack;
